import { useState } from "react";
import { RichTextEditor } from "../../../components/EmailTemplateEditor/RichTextEditor";
import { programService } from "../../../services/program.service";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../../components/common/PageHeader";
import { getRichTextLimitError } from "@/utils/richText";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function InviteAffiliates() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleMessageChange = (content: string) => {
    setMessage(content);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError("Please enter an email address");
      return;
    }

    const messageError = getRichTextLimitError(message, 6000);
    if (messageError) {
      setError(messageError);
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await programService.inviteAffiliate(email.trim(), message || undefined);
      setSuccess("Invitation sent successfully!");
      // Reset form after success
      setTimeout(() => {
        setEmail("");
        setMessage("");
        navigate("/brand/affiliates/active");
      }, 2000);
    } catch (err) {
      console.error("Failed to send invite:", err);
      setError("Failed to send invitation. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Invite affiliates" showDefaultActions={false} />

      {/* Form Section */}
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Email Input */}
        <div id="invite-affiliates-form-email" className="grid grid-cols-1 gap-2 sm:grid-cols-[200px,1fr] sm:gap-8 items-start">
          <div>
            <label className="block text-sm font-medium text-foreground">
              Email address
            </label>
          </div>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email address"
                className="pl-10"
                disabled={isLoading}
              />
              <div className="absolute left-3 top-1/2 -translate-y-1/2">
                <svg
                  width="20"
                  height="16"
                  viewBox="0 0 20 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18.3333 3.00001V13C18.3333 13.4603 18.1577 13.9017 17.8452 14.2142C17.5326 14.5268 17.0913 14.7024 16.631 14.7024H3.36921C2.90891 14.7024 2.46756 14.5268 2.15499 14.2142C1.84242 13.9017 1.66683 13.4603 1.66683 13V3.00001M18.3333 3.00001C18.3333 2.53972 18.1577 2.09837 17.8452 1.7858C17.5326 1.47323 17.0913 1.29764 16.631 1.29764H3.36921C2.90891 1.29764 2.46756 1.47323 2.15499 1.7858C1.84242 2.09837 1.66683 2.53972 1.66683 3.00001M18.3333 3.00001L10.7405 8.62175C10.5243 8.78079 10.2654 8.86644 10 8.86644C9.73458 8.86644 9.47573 8.78079 9.25952 8.62175L1.66683 3.00001"
                    stroke="#667085"
                    strokeWidth="1.66667"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Message Input */}
        <div id="invite-affiliates-form-message" className="grid grid-cols-1 gap-2 sm:grid-cols-[200px,1fr] sm:gap-8 items-start">
          <div>
            <label className="block text-sm font-medium text-foreground">
              Short message
            </label>
            <span className="text-sm text-muted-foreground mt-1">(optional)</span>
          </div>
          <div>
            <RichTextEditor
              content={message}
              onChange={handleMessageChange}
              placeholder="Write a short message to a potential affiliate about the invitation."
              maxLength={6000}
            />
          </div>
        </div>

        {/* Feedback Messages */}
        {error && (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-[200px,1fr] sm:gap-8">
            <div className="hidden sm:block"></div>
            <div className="p-3 bg-red-50 text-sm text-red-700 rounded-lg">{error}</div>
          </div>
        )}

        {success && (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-[200px,1fr] sm:gap-8">
            <div className="hidden sm:block"></div>
            <div className="p-3 bg-green-50 text-sm text-green-700 rounded-lg">
              {success}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-[200px,1fr] sm:gap-8 pt-4">
          <div className="hidden sm:block"></div>
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/brand/affiliates/active")}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              id="invite-affiliates-form-submit"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? "Sending invitation..." : "Send invitation"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
